/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons'
import type {
  DeltaDirection,
  StatDelta,
} from '@sportninja/common/features/profile/stats/types'
import { getDeltaColor } from '@sportninja/common/features/profile/stats/utils/seriesColors'
import { formatDeltaLabel } from '@sportninja/common/features/profile/stats/utils/statDelta'

import Text from 'src/components/ts-components/Text'

interface DeltaBadgeProps {
  delta: StatDelta
}

const ARROW_ICONS: Record<DeltaDirection, typeof ArrowUpOutlined> = {
  up: ArrowUpOutlined,
  down: ArrowDownOutlined,
}

const ICON_SIZE = 12

/**
 * Change against the previous window: a tinted pill, green better, red worse.
 * Screen readers also hear the previous value and window.
 */
const DeltaBadge = ({ delta }: DeltaBadgeProps) => {
  'use memo'

  const color = getDeltaColor(delta.tone, 'dark')
  const Arrow = ARROW_ICONS[delta.direction]
  return (
    <span
      role='img'
      aria-label={formatDeltaLabel(delta)}
      css={pillStyles(color)}
    >
      <Arrow aria-hidden='true' style={{ fontSize: ICON_SIZE, color }} />
      <Text variant='paragraphXSmall' weight='semiBold' color={color}>
        {delta.change}
      </Text>
    </span>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const pillStyles = (color: string) => css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 8px;
  background: ${color}26;
  white-space: nowrap;
`

export default DeltaBadge
